import { useContext, useMemo } from 'react';
import OwnerInfo from 'src/components/RealPropertyAppealForm/OwnerInfo';
import Qualifications from 'src/components/RealPropertyAppealForm/Qualifications/index';
import Step from 'src/components/Stepper/Step';
import Confirmation from 'src/components/util/Confirmation';
import FormUnavailable from 'src/components/util/FormUnavailable';
import Page from 'src/components/util/Page';
import FormContext from 'src/contexts/real-property-appeal-context';
import ownerInfoSchema from 'src/schemas/ownerInfoSchema';
import realPropertyQualificationSchema from 'src/schemas/realPropertyQualificationSchema';
import {
  isRealPropertyAppealAvailable,
  getRealPropertyAppealPeriod,
} from 'src/utils/dateRestrictions';

/**
 * A form with multiple steps for submitting a real property appeal.
 * It displays a confirmation message when the form has been successfully submitted.
 * Only available December 1 - January 31.
 *
 * @returns {JSX.Element} The RealPropertyAppealForm component.
 */
export default function RealPropertyAppealForm() {
  // Call hooks first (before any conditional returns)
  const { formData, isComplete, onBack, onNext, onSubmit, isLoading, contacts, disableNext } =
    useContext(FormContext);
  const formSteps = useMemo(
    () => [
      {
        component: <Qualifications />,
        label: 'Qualifications',
        schema: realPropertyQualificationSchema,
        isFirstStep: true,
        initialValues: {
          notCausedByCountyError: formData.notCausedByCountyError,
          paidInFull: formData.paidInFull,
          doesNotExceed2500: formData.doesNotExceed2500,
          penaltyDueToFailure: formData.penaltyDueToFailure,
          noPenaltyPriorThreeYears: formData.noPenaltyPriorThreeYears,
        },
      },
      {
        component: <OwnerInfo />,
        label: 'Owner Information',
        schema: ownerInfoSchema,
        initialValues: {
          owner: formData.owner,
        },
        isLastStep: true,
      },
    ],
    [formData]
  );

  // Check if form is available based on current date
  if (!isRealPropertyAppealAvailable()) {
    return (
      <FormUnavailable
        formName="Real Property Appeal Form"
        availablePeriod={getRealPropertyAppealPeriod()}
        contacts={contacts}
      />
    );
  }

  if (isComplete) {
    return (
      <Confirmation
        text="Your Real Property Appeal Form has successfully been submitted!"
        contacts={contacts}
      />
    );
  }

  return (
    <Page pageContext={FormContext}>
      {formSteps.map((step) => (
        <Step
          key={step.label}
          {...step}
          disableNext={disableNext}
          onBack={onBack}
          onNext={onNext}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />
      ))}
    </Page>
  );
}
